// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Always sync access token to window (used by axios interceptor)
  useEffect(() => {
    if (accessToken) {
      window.accessToken = accessToken;
      console.log('✅ Token synced to window.accessToken');
    } else {
      window.accessToken = null;
      console.log('❌ Token cleared from window.accessToken');
    }
  }, [accessToken]);

  // ✅ Listen for token refresh or auth error from axios interceptor
  useEffect(() => {
    const handleTokenRefresh = (event) => {
      console.log('🔄 Token refresh event received');
      setAccessToken(event.detail.token);
      if (event.detail.user) {
        setUser(event.detail.user);
        sessionStorage.setItem('user', JSON.stringify(event.detail.user));
      }
      sessionStorage.setItem('accessToken', event.detail.token);
    };

    const handleAuthError = (event) => {
      console.warn('⚠️ Auth error:', event.detail.message);
      logout();
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);
    window.addEventListener('authError', handleAuthError);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
      window.removeEventListener('authError', handleAuthError);
    };
  }, []);

  // ✅ Initialize authentication on app load
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      console.log('🚀 Initializing auth...');
      const token = sessionStorage.getItem('accessToken');
      const userData = JSON.parse(sessionStorage.getItem('user'));

      if (token && userData) {
        setAccessToken(token);
        setUser(userData);
        console.log('✅ Loaded auth from sessionStorage');
        return;
      }

      // Try to refresh token on startup
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api'}/users/refresh`,
        {},
        { withCredentials: true }
      );

      if (response.data.accessToken) {
        console.log('🔄 Auth initialized via refresh');
        setAccessToken(response.data.accessToken);
        setUser(response.data.user);
        sessionStorage.setItem('accessToken', response.data.accessToken);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
      }
    } catch (error) {
      console.log('❌ No active session found');
      setUser(null);
      setAccessToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Login and persist
  const login = (token, userData) => {
    console.log('🔐 Logging in:', userData?.email);
    setAccessToken(token);
    setUser(userData);
    sessionStorage.setItem('accessToken', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    window.accessToken = token; // Ensure axios gets it immediately
  };

  // ✅ Logout and cleanup
  const logout = async () => {
    try {
      console.log('🚪 Logging out...');
      await axios.post(
        `${process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api'}/users/logout`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAccessToken(null);
      setUser(null);
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('user');
      window.accessToken = null;
    }
  };

  // Optional: manual refresh trigger
  const refreshAccessToken = async () => {
    try {
      console.log('🔄 Manually refreshing token...');
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api'}/users/refresh`,
        {},
        { withCredentials: true }
      );

      if (response.data.accessToken) {
        setAccessToken(response.data.accessToken);
        sessionStorage.setItem('accessToken', response.data.accessToken);
        return response.data.accessToken;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  };

  const value = {
    user,
    accessToken,
    isLoading,
    login,
    logout,
    refreshAccessToken,
    isAuthenticated: !!accessToken,
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
